import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography, useTheme, Grid2, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { FaRegEdit } from "react-icons/fa";
import moment from 'moment';
import { MaterialReactTable } from 'material-react-table';
import { useDispatch } from 'store';
import { useGetAllDataAlertsQuery } from 'store/slices/apis/dataAlertManagementSlice';
import { useParams } from 'react-router-dom';
import EditReportData from './EditReportData';
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import {ReactComponent as SearchIcon} from "assets/PwC_Funct_Icons_Search_Outline_Black_RGB.svg"

const buildFiltersFromJson = (filters = []) => {
    const columnFilters = [];
    const filterOptions = {};
    const filterDayRange = {};

    filters.forEach((f) => {

        if (f.type === 'Date' && Array.isArray(f.value)) {
            columnFilters.push({ id: f.column });
            filterOptions[f.column] = "Date Range";
            filterDayRange[f.column] = {
                start: parseInt(f.value[0]),
                end: parseInt(f.value[1]),
            };
        }
        else if (f.type === 'Date' && !Array.isArray(f.value)) {

            columnFilters.push({ id: f.column });
            filterOptions[f.column] = f.value; // for non-date
        }
    });

    return { columnFilters, filterOptions, filterDayRange };
};

const formatDate = (d) => (d ? moment(d).format('DD/MM/YY') : '');

const MyReport = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const [pageNumber, setPageNumber] = useState(1);
    const pageSize = 10;
    const [displayData, setDisplayData] = useState([]);
    const [isEditIssueModal, setEditIssueModal] = useState(false);
    const [issueData, setEditIssueData] = useState(null);

    const { data, isLoading } = useGetAllDataAlertsQuery({
        ProjectId: projectId,
        //page: pageNumber,
        //pageSize,
    });

    useEffect(() => {
        if (isLoading) {
            setDisplayData([]);
            return;
        }

        if (!data?.data?.length) return;

        const rows = data.data
            .filter((x) => x.dataMetric === 'Report')
            .map((item, idx) => {
                let parsed = {};
                let filtersString = '-';
                let sortingString = '';
                let displayString = '';
                try {
                    parsed = item.filterJson ? JSON.parse(item.filterJson) : {};
                    if (Array.isArray(parsed.filters)) {
                        filtersString = parsed.filters
                            .map((f) => {
                                if (f.type === 'Date' && Array.isArray(f.value)) {
                                    const [startDay, endDay] = f.value;
                                    return `${f.column}: (Day ${startDay}-${endDay})`;
                                } else {
                                    return `${f.column}: ${f.value}`;
                                }
                            }
                            )
                            .join(' | ');
                    }

                    if (Array.isArray(parsed.sorting) && parsed.sorting.length > 0) {
                        sortingString = parsed.sorting
                            .map((s) => `${s.column}: ${s.order === 'Descending' ? 'Descending' : 'Ascending'}`)
                            .join(' | ');
                    }

                    if (filtersString) displayString += `Filters → ${filtersString}`;
                    if (sortingString) displayString += `${filtersString ? ' || ' : ''}Sorting → ${sortingString}`;

                } catch (e) {
                    console.error('Invalid JSON in filterJson', e);
                }

                return {
                    ...item,
                    //   sr_no: (pageNumber - 1) * pageSize + idx + 1,
                    reportname: parsed.reportname || '-',
                    module: parsed.module || '-',
                    filters: displayString,
                    last_ran: formatDate(item.startDate),
                    next_ran: formatDate(item.endDate),
                };
            });

        setDisplayData(rows);
    }, [data, isLoading, pageNumber, pageSize]);

    const columns = useMemo(
        () => [
            //   {
            //     accessorKey: 'sr_no',
            //     header: 'S No.',
            //     size: 75,
            //     muiTableHeadCellProps: { align: 'center' },
            //     muiTableBodyCellProps: { align: 'center' },
            //   },
            {
                accessorKey: 'reportname',
                header: 'Report Name',
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center', sx: { whiteSpace: 'nowrap' }, },
            },
            {
                accessorKey: 'module',
                header: 'Module',
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            },
            {
                accessorKey: 'filters',
                header: 'Filters',
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: {
                    align: 'center',
                    sx: { whiteSpace: 'nowrap' },
                },
            },
            {
                accessorKey: 'last_ran',
                header: 'Last Ran',
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            },
            {
                accessorKey: 'next_ran',
                header: 'Next Ran',
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
            },
            {
                id: 'actions',
                header: 'Action',
                size: 80,
                muiTableHeadCellProps: { align: 'center' },
                muiTableBodyCellProps: { align: 'center' },
                Cell: ({ row }) => (
                    <Tooltip title="Edit Report" placement="top">
                        <IconButton
                            size="small"
                            onClick={() => handleEditOpen(row.original)}
                            aria-label="edit report"
                        >
                            <FaRegEdit fontSize="inherit" />
                            {/* <EditIcon fontSize="inherit" /> */}
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [theme],
    );

    const openNotification = async (msg, type = "error") => {
        await dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: "alert",
                alert: {
                    color: type,
                },
                close: false,
            })
        );

        setTimeout(() => dispatch(closeSnackbar()), 3000);
    };

    const handleEditOpen = (row) => {
        let parsed = {};
        try {
            parsed = row.filterJson ? JSON.parse(row.filterJson) : {};
        } catch (e) {
            console.error('Invalid JSON', e);
        }

        const { columnFilters, filterOptions, filterDayRange } =
            buildFiltersFromJson(parsed.filters);

        const initialFormData = {
            report_name: parsed.reportname ?? '',
            module: parsed.module ?? '',
            interval: row.interval ?? '',
            repeat: row.repeat ?? '',
            weekday: row.day ?? '',
            month: row.month ?? 'March',
            day: row.date ?? '',
            start_date: row.startDate ?? '',
            end_date: row.endDate ?? '',
            recipient: row.roles ?? [],
            filterOptions,
            filterDayRange,
            allFilters: parsed.filters || [],
            sorting: parsed.sorting || [],
        };

        setEditIssueData({
            id: row.id,
            initialFormData,
            columnFilters,
        });
        setEditIssueModal(true);
    };

    const handleEditClose = () => {
        setEditIssueModal(false);
        setEditIssueData(null);
    };

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: 2 }}>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: 24,
                            fontWeight: 500,
                            color: '#252525',
                            mb: 2,
                            lineHeight: '32px',
                            letterSpacing: '-0.4px',
                        }}
                    >
                        My Reports
                    </Typography>
                </Box>
            </Grid2>

            <Box
                sx={{
                    mt: 1,
                    width: '100%',
                    bgcolor: '#fff',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <MaterialReactTable
                        columns={columns}
                        data={displayData}
                        enableRowNumbers
                        rowNumberDisplayMode="static"
                        displayColumnDefOptions={{
                            'mrt-row-numbers': {
                                header: 'S No',
                                size: 75,
                                muiTableHeadCellProps: { align: 'center' },
                                muiTableBodyCellProps: { align: 'center' },
                            },
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                fontWeight: 500,
                                fontSize: 14,
                                color: '#505258',
                                p: '10px 14px',
                                bgcolor: '#F8F8F8',
                                border: '1px solid #D8D8D8',
                            },
                        }}
                        muiTableBodyRowProps={{
                            sx: {
                                fontSize: 14,
                                color: '#505258',
                                p: '10px 14px',
                                border: '1px solid #D8D8D8',
                            },
                        }}
                        enableExpanding={false}
                        enableRowSelection={false}
                        enableFullScreenToggle={false}
                    
                    />
                )}
            </Box>

            {isEditIssueModal && (
                <EditReportData
                    open={isEditIssueModal}
                    onClose={handleEditClose}
                    data={issueData}
                    openNotification={openNotification}
                    columns={columns}
                />
            )}
        </>
    );
};

export default MyReport;